import { isEqual, uniq } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  MatchableKind,
  SuggestableKind,
} from '@/modules/aiSuggestions/aiSuggestions.types';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { getSuggestionEvidenceDocumentIds } from '@/modules/aiSuggestions/evidence';
import { CommonAiSuggestionFragment } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import {
  processTestamentaryEntitySuggestionsData,
  useFetchTestamentaryEntityAISuggestions,
} from '@/modules/aiSuggestions/hooks/useFetchTestamentaryEntityAISuggestions';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useEditTestamentaryEntitySplitScreenModalContext } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/contexts/editTestamentaryEntitySplitScreenModal.context';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { getAISuggestionsForTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.utils';
import { AiSuggestionKind } from '@/types/schema';

import {
  TestamentaryEntitySuggestionsContext,
  TestamentaryEntitySuggestionsContextType,
} from './TestamentaryEntitySuggestions.context';

function useContextValue(): TestamentaryEntitySuggestionsContextType {
  const { testamentaryEntityId } =
    useEditTestamentaryEntitySplitScreenModalContext();
  const shouldSeeSuggestions = useCanEditHouseholdData();
  const { initPreviouslyAcknowledgedSuggestions, getAcknowledgedSuggestion } =
    useSuggestionAcknowledgementContext();

  const [suggestionsByKind, setSuggestionsByKind] = useState<
    Record<AiSuggestionKind, CommonAiSuggestionFragment[]>
  >({} as Record<AiSuggestionKind, CommonAiSuggestionFragment[]>);

  const previousSuggestionsRef = useRef<CommonAiSuggestionFragment[]>([]);

  const { refetch, loading } = useFetchTestamentaryEntityAISuggestions({
    testamentaryEntityId,
    skip: !testamentaryEntityId || !shouldSeeSuggestions,
    onCompleted: (data) => {
      const { suggestions, groupedByKind } =
        processTestamentaryEntitySuggestionsData(data);

      // Check if suggestions have actually changed to prevent unnecessary updates
      const suggestionsChanged = !isEqual(
        previousSuggestionsRef.current,
        suggestions
      );

      if (!suggestionsChanged) {
        return;
      }

      // Update the ref with the current suggestions
      previousSuggestionsRef.current = [...suggestions];

      // Initialize previously acknowledged suggestions
      initPreviouslyAcknowledgedSuggestions(suggestions);

      setSuggestionsByKind(groupedByKind);
    },
  });

  const getCurrentSectionSuggestions = useCallback(
    (currentSection: EditTestamentaryEntitySection) => {
      return getAISuggestionsForTestamentaryEntitySection(
        currentSection,
        suggestionsByKind
      );
    },
    [suggestionsByKind]
  );

  const getSuggestableSuggestion = useCallback(
    (kind: SuggestableKind) => {
      return suggestionsByKind[kind]?.[0];
    },
    [suggestionsByKind]
  );

  const getMatchableSuggestion = useCallback(
    (kind: MatchableKind, matchedId: string) => {
      const matched = getAcknowledgedSuggestion(matchedId);
      const byKind = suggestionsByKind[kind];
      return (byKind || []).find((b) =>
        matched.some((m) => b.id === m.suggestionID)
      );
    },
    [getAcknowledgedSuggestion, suggestionsByKind]
  );

  // Extract file-paragraph document IDs for the document viewer.
  const suggestionDocumentIds = useMemo(() => {
    const allSuggestions = Object.values(suggestionsByKind).flat();
    return uniq(
      allSuggestions.flatMap((s) =>
        getSuggestionEvidenceDocumentIds(s.evidence)
      )
    );
  }, [suggestionsByKind]);

  return {
    suggestionsByKind,
    getCurrentSectionSuggestions,
    getSuggestableSuggestion,
    getMatchableSuggestion,
    refetchSuggestions: refetch,
    loading,
    suggestionDocumentIds,
  };
}

export function TestamentaryEntitySuggestionsProvider({
  children,
}: PropsWithChildren) {
  const value = useContextValue();

  return (
    <TestamentaryEntitySuggestionsContext.Provider value={value}>
      {children}
    </TestamentaryEntitySuggestionsContext.Provider>
  );
}
